import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Box,
  Typography,
  Chip
} from '@mui/material';
import { savePattern } from '../utils/patternManager';
import { track } from '../utils/analytics';

const SavePatternDialog = ({
  open,
  onClose,
  patternData,
  onSaveSuccess
}) => {
  const [patternTitle, setPatternTitle] = useState('');
  const [error, setError] = useState('');
  
  const handleSave = () => {
    // Validate title
    if (!patternTitle.trim()) {
      setError('Please enter a preset title');
      return;
    }
    
    const patternToSave = {
      ...patternData,
      title: patternTitle.trim()
    };

    const patternId = savePattern(patternToSave);

    if (patternId) {

      // Reset form and close dialog
      setPatternTitle('');
      setError('');

      // Dispatch an event to notify that a pattern was saved
      // This will be used to refresh the pattern list in PatternsPanel
      const patternSavedEvent = new Event('patternSaved');
      window.dispatchEvent(patternSavedEvent);


      track('pattern_saved', { root_note: patternData?.rootNote, mode: patternData?.currentMode, title: patternTitle.trim() });

      if (onSaveSuccess) {
        onSaveSuccess(patternId);
      }
      onClose();
    } else {
      // Show error
      setError('Failed to save preset. Please try again.');
    }
  };
  
  const handleClose = () => {
    // Reset form
    setPatternTitle('');
    setError('');
    onClose();
  };
  
  return (
    <Dialog
      className="save-pattern-dialog"
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          borderRadius: '10px'
        }
      }}
    >
      <DialogTitle>Save Preset</DialogTitle>
      <DialogContent>
        <TextField
          label="Preset Title"
          value={patternTitle}
          onChange={(e) => setPatternTitle(e.target.value)}
          fullWidth
          margin="dense"
          autoFocus
          error={!!error}
          helperText={error}
          sx={{ mb: 2 }}
        />

        {patternData && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preset information:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              <Chip
                label={`${patternData.rootNote} ${patternData.currentMode}`}
                color="secondary"
                size="small"
                onClick={() => setPatternTitle(prev => prev ? `${prev} ${patternData.rootNote} ${patternData.currentMode}` : `${patternData.rootNote} ${patternData.currentMode}`)}
                sx={{ cursor: 'pointer' }}
              />
              {patternData.currentProgression && (
                <Chip
                  label={`Progression: ${patternData.currentProgression}`}
                  color="primary"
                  size="small"
                  onClick={() => setPatternTitle(prev => prev ? `${prev} ${patternData.currentProgression}` : patternData.currentProgression)}
                  sx={{ cursor: 'pointer' }}
                />
              )}
              <Typography variant="caption" sx={{ ml: 1 }}>
                {patternData.highlightTriads ? 'Triad highlighting enabled' : ''}
                {patternData.highlightTriads && patternData.showConnectionLines ? ' • ' : ''}
                {patternData.showConnectionLines ? 'Connection lines enabled' : ''}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SavePatternDialog;
